import React from 'react'
import styled from 'styled-components'

import { useMediaQueryContext } from 'contexts/MediaQueryContext'
import { media, mediaMax } from 'styles/media'
import { useGetBucketListItemsIndexQuery } from 'hooks/useQueries'

import Container from '../../../../components/layout/Container'
import {
  Grid,
  GridItem as BaseGridItem,
} from '../../../../components/layout/ItemGrid'
import PlaceholderCard from '../../../../components/cards/PlaceholderCard'
import SimpleLink from '../../../../components/display/SimpleLink'
import StoryCard from '../../../../components/cards/StoryCard'
import WithSelectEscape from '../../../../components/SelectEscape/WithSelectEscape'

import SaveYourFaves from '../../../aboutStories/Customer/iconography/SaveYourFaves'
import GetOutOfTown from '../../../aboutStories/Customer/iconography/GetOutOfTown'
import ReflectAndReview from '../../../aboutStories/Customer/iconography/ReflectAndReview'

import { CallToActionButton, Title, Subtitle } from '../../shared'

import { defaultStateCodeForMarket } from '../../utils'

const GridItem = styled(BaseGridItem).attrs({
  widthOverride: {
    xxxl: '33.33%',
  },
})``

const HeadingsWrapper = styled.div`
  ${mediaMax.md`
    padding-left: 16px;
    padding-right: 16px;
  `}
`

const StoryListWrapper = styled.div`
  ${media.md`
    width: 100%;
    max-width: 1300px;
    margin: 0 auto;
  `}
`

type Story = GQL_StoryCardFragmentFragment & {
  featuredListing?: GQL_StoryFeaturedListingFragmentFragment
}

const Loader = React.memo(() => (
  <>
    {[...Array(6)].map((e, i) => (
      // eslint-disable-next-line react/no-array-index-key
      <GridItem key={i}>
        <PlaceholderCard />
      </GridItem>
    ))}
  </>
))

const EmptyStateIllustrationWrapper = styled(GridItem)`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 48px;

  svg {
    max-width: 240px;
  }
`

type FeaturedStoriesProps = {
  marketCode: GQL_MarketCode
  countryCode: string
  loading: boolean
  isEmpty?: boolean
  stories: Story[]
}

const FeaturedStories = ({
  marketCode,
  countryCode = 'AU',
  loading,
  isEmpty,
  stories,
}: FeaturedStoriesProps) => {
  const { data: bucketListIndexData } = useGetBucketListItemsIndexQuery({
    fetchPolicy: 'cache-first',
  })

  const bucketListItemsIndex = bucketListIndexData?.currentUser
    ?.bucketListItemsIndex as GQL_BucketListItemsIndex

  const { lg: isLg } = useMediaQueryContext()

  const stateParam = defaultStateCodeForMarket(marketCode)

  const ctaPathname = isEmpty ? '/stories-travellers' : `/listings`

  const ctaSearch = isEmpty
    ? ''
    : `?type=accommodation&country=${countryCode}&state=${stateParam}`

  const ctaText = isEmpty ? 'Learn More About Stories' : 'Discover More Stories'

  const title = isEmpty ? 'Stories Coming Soon' : 'Explore Stories'

  return (
    <>
      <StoryListWrapper>
        <HeadingsWrapper>
          <Title>{title}</Title>

          {!isEmpty && (
            <Subtitle>
              Our latest Stories, all inspired by a love of the outdoors
            </Subtitle>
          )}

          {isEmpty && (
            <>
              <Subtitle>
                We have sent our storytellers out on a mission to bring you some
                authentic travel experiences to inspire your next soul-fulilling
                escape.
              </Subtitle>

              <Subtitle>
                Keep an eye out for some stories to start showing up here soon.
              </Subtitle>
            </>
          )}
        </HeadingsWrapper>

        <Grid>
          {loading && <Loader />}

          {isEmpty && (
            <>
              <EmptyStateIllustrationWrapper>
                <SaveYourFaves />
              </EmptyStateIllustrationWrapper>

              <EmptyStateIllustrationWrapper>
                <GetOutOfTown />
              </EmptyStateIllustrationWrapper>

              <EmptyStateIllustrationWrapper>
                <ReflectAndReview />
              </EmptyStateIllustrationWrapper>
            </>
          )}

          {!loading &&
            stories?.map((story) => {
              return (
                <GridItem key={story.id}>
                  <WithSelectEscape
                    itemId={story.id}
                    itemType="Story"
                    bucketListItemsIndex={bucketListItemsIndex}
                  >
                    <StoryCard
                      story={story}
                      listing={story.featuredListing}
                      sameTab={!isLg}
                    />
                  </WithSelectEscape>
                </GridItem>
              )
            })}
        </Grid>
      </StoryListWrapper>

      <Container>
        <SimpleLink
          to={{
            pathname: ctaPathname,
            search: ctaSearch,
            state: {
              scrollTop: true,
            },
          }}
        >
          <CallToActionButton as="div" ghost={isEmpty} rounded>
            {ctaText}
          </CallToActionButton>
        </SimpleLink>
      </Container>
    </>
  )
}

export default FeaturedStories
